import { FormConfig } from '@lbcamden/lib-react';
import { DateTime, Duration } from 'luxon';
import { nanoid } from 'nanoid';
import { z } from 'zod';

import { visitOutcomeDisplayValues } from '~/client/config/display-values/visits';
import { useCurrentUser } from '~/client/hooks/storeHooks';
import { presentOrPastDateSchema } from '~/common/schemas/data';
import { visitOutcomeSchema, visitSchema } from '~/common/schemas/visits';
import { PropertyVisit } from '~/common/types/visits';
import { selectOptionsFromDisplayValues } from '~/common/utils/data';

export type VisitFormValues = z.infer<typeof visitFormSchema>;

const outcomeOptions = selectOptionsFromDisplayValues(visitOutcomeDisplayValues);

export const visitFormSchema = z
  .object({
    officerName: visitSchema.shape.officerName,
    date: presentOrPastDateSchema('Please enter the date of visit'),
    time: z.iso.time('Please enter the time of visit'),
    outcome: visitOutcomeSchema,
    notes: visitSchema.shape.notes,
  })
  .refine(({ date, time }) => DateTime.fromISO(`${date}T${time}`) <= DateTime.now(), {
    message: 'Date and time cannot be in the future',
    path: ['time'],
  });

/**
 * Function (not a constant) so that `max` date is evaluated at render time, not module load
 * important for long-running PWA sessions.
 * */
export const getVisitFormConfig = (): FormConfig<VisitFormValues> => ({
  fieldConfigs: {
    officerName: {
      label: 'Officer name',
      readOnly: true,
    },
    date: {
      label: 'Date of visit',
      type: 'date',
      inputProps: { max: DateTime.now().toISODate() },
    },
    time: {
      label: 'Time of visit',
      type: 'time',
    },
    outcome: {
      label: 'Outcome',
      selectOptions: outcomeOptions,
    },
    notes: {
      label: 'Notes',
      type: 'textarea',
    },
  },
});

export const useConvertVisitFormData = (ids: { propertyId: number; visitId?: string }) => {
  const user = useCurrentUser();

  if (!user) {
    throw Error('expected user to be logged in');
  }

  return {
    toForm: ({ timestamp, ...data }: Partial<PropertyVisit>): Partial<VisitFormValues> => ({
      ...data,
      officerName: data.officerName || user.name,
      date: timestamp && DateTime.fromISO(timestamp).toISODate()!,
      time: timestamp && DateTime.fromISO(timestamp).toFormat('HH:mm'),
    }),
    fromForm: (
      { date, time, ...data }: VisitFormValues,
      currentValues?: PropertyVisit,
    ): PropertyVisit => ({
      ...data,
      visitId: currentValues?.visitId || ids.visitId || nanoid(),
      propertyId: currentValues?.propertyId || ids.propertyId,
      timestamp: DateTime.fromISO(date).plus(Duration.fromISOTime(time)).toUTC().toISO()!,
      officerId: currentValues?.officerId || user.id,
      officerCamdenAccountName: currentValues?.officerCamdenAccountName || user.camdenAccountName,
    }),
  };
};
